import { useState } from 'react';
import { CalendarDays, CalendarPlus, Users } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { QueryError } from '@/components/ui/QueryError';
import { Skeleton } from '@/components/ui/Skeleton';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useRefreshKey } from '@/hooks/useRefreshKey';
import { toast } from '@/hooks/useToast';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { ApiRequestError } from '@/lib/apiClient';
import { cn } from '@/lib/utils';
import { parentApi } from '../api/parent.api';
import { ApplyLeaveModal } from '@/components/leave/ApplyLeaveModal';
import { AttendanceCalendarCard } from '../components/attendance/AttendanceCalendarCard';
import { AttendanceHistoryCard } from '../components/attendance/AttendanceHistoryCard';
import { AttendanceMetricCards } from '../components/attendance/AttendanceMetricCards';
import { AttendanceNoteBanner } from '../components/attendance/AttendanceNoteBanner';
import { AttendanceReportView } from '../components/attendance/AttendanceReportView';
import { LeaveRequestsCard } from '@/components/leave/LeaveRequestsCard';
import { useParentChild, useSelectedChild } from '../store/child.store';
import { PARENT_PALETTE } from '../theme';
import type { StudentLeaveRequest } from '@/types/leave';
import { formatLongDay, todayIso } from '../utils';

const ATTENDANCE_TABS = ['attendance', 'report', 'leave'] as const;
type AttendanceTab = (typeof ATTENDANCE_TABS)[number];

/**
 * The open tab and month, as they sit in the address bar.
 *
 * An empty month means the parent has not picked one — the server answers
 * with the latest month of the session, which is what someone opening the
 * screen wants.
 */
type Filters = {
  tab: AttendanceTab;
  month: string;
};

const DEFAULT_FILTERS: Filters = { tab: 'attendance', month: '' };

const FILTER_OPTIONS = {
  allowed: { tab: ATTENDANCE_TABS },
};

/** `2026-05`, as the server reads it. Anything else in a link is no month picked. */
const MONTH_KEY = /^\d{4}-(0[1-9]|1[0-2])$/;


/**
 * One child's register, as their family reads it.
 *
 * Every figure here is the register their teacher actually took — the calendar,
 * the counts and the percentage all come from `GET /parent/children/:uuid/
 * attendance`, which builds the month against the school's own working week and
 * holiday list. Nothing on this screen is worked out in the browser, because
 * deciding whether a blank day is a Sunday, a declared holiday or a register
 * nobody took needs data the client does not have.
 *
 * The month lives here rather than in the two cards that change it: the
 * calendar, the history table and the report tab must all be showing the same
 * month, and three copies of that state is three ways for them to drift apart.
 */
export function ParentAttendancePage() {
  const child = useSelectedChild();
  const isLoadingChildren = useParentChild((state) => state.isLoading);
  const childrenError = useParentChild((state) => state.error);
  const reloadChildren = useParentChild((state) => state.load);

  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  const activeTab = filters.tab;
  const monthKey = MONTH_KEY.test(filters.month) ? filters.month : undefined;
  const setActiveTab = (tab: AttendanceTab) => setFilters({ tab });
  const setMonthKey = (month: string) => setFilters({ month });
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState<boolean>(false);
  const [withdrawing, setWithdrawing] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useApiQuery(
    () => (child ? parentApi.attendance(child.uuid, monthKey) : Promise.resolve(null)),
    [child?.uuid, monthKey],
  );

  // Its own request rather than part of the month: leave is not filed against a
  // month, and a family switching from May to April must not lose sight of the
  // request they are waiting on.
  //
  // Read again on every arrival and whenever the bell finds a new notification
  // pointing here, so a decision shows without a manual refresh. As on the
  // student's leave tab.
  const refreshKey = useRefreshKey();
  const {
    data: leaveRequests,
    isLoading: isLoadingLeave,
    error: leaveError,
    refetch: refetchLeave,
  } = useApiQuery(
    () => (child ? parentApi.leaveRequests(child.uuid) : Promise.resolve(null)),
    [child?.uuid, refreshKey],
  );

  /**
   * Taking back a request nobody has answered yet.
   *
   * The register is untouched either way — approving leave records a decision,
   * it does not mark a day, so there is nothing here to undo.
   */
  async function withdraw(request: StudentLeaveRequest) {
    if (!child) return;
    setWithdrawing(request.uuid);

    try {
      await parentApi.cancelLeave(child.uuid, request.uuid);
      refetchLeave();
    } catch (caught) {
      // The server names the reason — most often that the class teacher
      // answered it while this page was open.
      toast.error(
        'Could not withdraw that request',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
      refetchLeave();
    } finally {
      setWithdrawing(null);
    }
  }

  if (isLoadingChildren) {
    return (
      <div className={cn(PARENT_PALETTE, 'flex flex-col gap-6')}>
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-32 rounded-3xl" />
          ))}
        </div>
        <Skeleton className="h-96 rounded-3xl" />
      </div>
    );
  }

  if (childrenError) {
    return (
      <div className={cn(PARENT_PALETTE)}>
        <QueryError
          title="Could not load your children"
          error={new ApiRequestError(childrenError, 0, 'INTERNAL_ERROR')}
          onRetry={() => void reloadChildren()}
        />
      </div>
    );
  }

  if (!child) {
    return (
      <div className={cn(PARENT_PALETTE, 'card')}>
        <EmptyState
          icon={<Users className="h-5 w-5" />}
          title="No child linked to your account yet"
          description="Your school office links a parent's account to their child's record. Once that is done, your child's attendance and academic records appear here."
        />
      </div>
    );
  }

  return (
    <div className={cn(PARENT_PALETTE, 'flex flex-col gap-6')}>
      {/* ---------------- Page Header & Tabs ---------------- */}
      <header className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h1 className="text-2xl  font-bold tracking-tight text-slate-900 dark:text-white">
            Attendance
          </h1>

          {/* Today Indicator & Action */}
          <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-100">
              <CalendarDays className="h-4 w-4 text-slate-600 dark:text-slate-300" />
              {/* The school's day in India, not the device's — the same day the
                  server builds the calendar's "today" from. */}
              <span>Today is {formatLongDay(todayIso())}</span>
            </div>

            {activeTab === 'leave' && (
              <Button
                size="sm"
                onClick={() => setIsLeaveModalOpen(true)}
                leftIcon={<CalendarPlus className="h-4 w-4" />}
              >
                Apply for leave
              </Button>
            )}
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex items-center gap-6 border-b border-slate-200 dark:border-white/10 pb-0 overflow-x-auto">
          <button
            type="button"
            onClick={() => setActiveTab('attendance')}
            className={`pb-3 text-base font-semibold transition-all relative cursor-pointer ${
              activeTab === 'attendance'
                ? 'text-indigo-600 dark:text-indigo-400'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            Attendance
            {activeTab === 'attendance' && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-indigo-400 rounded-full" />
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('report')}
            className={`pb-3 text-base font-semibold transition-all relative cursor-pointer ${
              activeTab === 'report'
                ? 'text-indigo-600 dark:text-indigo-400'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            Attendance Report
            {activeTab === 'report' && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-indigo-400 rounded-full" />
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('leave')}
            className={`pb-3 text-base font-semibold transition-all relative cursor-pointer ${
              activeTab === 'leave'
                ? 'text-indigo-600 dark:text-indigo-400'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            Leave Requests
            {activeTab === 'leave' && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-indigo-400 rounded-full" />
            )}
          </button>
        </div>
      </header>

      {activeTab !== 'leave' && error && (
        <QueryError
          title={`Could not load ${child.fullName}'s attendance`}
          error={error}
          onRetry={refetch}
        />
      )}

      {/* The month is fetched again on every change, so the cards stay on screen
          and dim rather than collapsing to a skeleton each time. */}
      {activeTab !== 'leave' && !data && isLoading && (
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }, (_, index) => (
              <Skeleton key={index} className="h-32 rounded-3xl" />
            ))}
          </div>
          <Skeleton className="h-96 rounded-3xl" />
        </div>
      )}

      {activeTab !== 'leave' && data && (
        <div className={cn('flex flex-col gap-5', isLoading && 'opacity-60 transition-opacity')}>
          {activeTab === 'attendance' ? (
            <>
              {/* 4 Metric Cards */}
              <AttendanceMetricCards stats={data.stats} monthLabel={data.label} />

              {/* Two Columns: Calendar (Left) & History Table (Right) */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                {/* Left: Attendance Calendar */}
                <div className="lg:col-span-5">
                  <AttendanceCalendarCard
                    months={data.months}
                    selectedMonthKey={data.monthKey}
                    onMonthChange={setMonthKey}
                    calendarDays={data.calendarDays}
                  />
                </div>

                {/* Right: Attendance History */}
                <div className="lg:col-span-7">
                  <AttendanceHistoryCard
                    records={data.records}
                    months={data.months}
                    selectedMonthKey={data.monthKey}
                    onMonthChange={setMonthKey}
                  />
                </div>
              </div>

              {/* Bottom Note */}
              <AttendanceNoteBanner />
            </>
          ) : (
            <AttendanceReportView
              child={child}
              monthData={data}
              onMonthChange={setMonthKey}
            />
          )}
        </div>
      )}

      {activeTab === 'leave' && (
        <LeaveRequestsCard
          requests={
            leaveRequests
              ? [...leaveRequests].sort(
                  (a, b) =>
                    new Date(b.requestedOn).getTime() - new Date(a.requestedOn).getTime(),
                )
              : null
          }
          isLoading={isLoadingLeave}
          error={leaveError}
          onRetry={refetchLeave}
          onApply={() => setIsLeaveModalOpen(true)}
          onWithdraw={(request) => void withdraw(request)}
          withdrawing={withdrawing}
        />
      )}

      {/* Leave Application Modal */}
      <ApplyLeaveModal
        isOpen={isLeaveModalOpen}
        onClose={() => setIsLeaveModalOpen(false)}
        child={child}
        onSubmit={(payload) =>
          child ? parentApi.requestLeave(child.uuid, payload) : Promise.resolve(null)
        }
        onSubmitted={refetchLeave}
      />
    </div>
  );
}
